import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { isArray } from "../../lib/utility";

const PRESET_ROW_CLASS =
  "filterHeaderPresetSelect__option d-flex align-items-center py-1 px-2";

function FilterHeaderPresetSelect({
  value,
  onChange,
  options,
  dropdownStyle = {},
  triggerStyle = {},
  disabled,
  placeholder,
}) {
  const { t } = useTranslation("common");
  const [focused, setFocused] = useState(false);
  const selectRef = useRef(null);
  const dropdownRef = useRef(null);

  const sanitizedOptions = isArray(options)
    ? options.filter((opt) => opt && opt.id !== undefined && opt.id !== null)
    : [];

  const selectedOption = sanitizedOptions.find(
    (opt) => String(opt.id) === String(value ?? "")
  );
  const defaultPlaceholder = t("commonUi.custom", { defaultValue: "Custom" });
  const displayLabel =
    selectedOption?.label ??
    (value && String(value).trim() !== "" ? String(value) : (placeholder || defaultPlaceholder));

  const { width, height, y, x } =
    selectRef?.current?.getBoundingClientRect() ?? {};
  const above = !focused
    ? false
    : window.innerHeight - y - height - 3 < Math.min(200, sanitizedOptions.length * 36 + 16);

  const handleOutSideClick = useCallback((e) => {
    if (
      selectRef.current?.contains(e.target) ||
      dropdownRef.current?.contains(e.target)
    ) {
      return;
    }
    setFocused(false);
  }, []);

  useEffect(() => {
    if (!focused) return;
    window.addEventListener("click", handleOutSideClick);
    return () => {
      window.removeEventListener("click", handleOutSideClick);
    };
  }, [focused, handleOutSideClick]);

  useEffect(() => {
    if (!focused) return;

    const handleScroll = (e) => {
      if (
        selectRef.current?.contains(e.target) ||
        dropdownRef.current?.contains(e.target)
      ) {
        return;
      }
      setFocused(false);
    };

    window.addEventListener("scroll", handleScroll, true);
    return () => {
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [focused]);

  const handleTriggerClick = () => {
    if (disabled) return;
    setFocused((prev) => !prev);
  };

  const { scale: dropdownScale, ...dropdownStyleRest } = dropdownStyle ?? {};
  const dropdownTransform =
    dropdownScale != null && dropdownScale !== ""
      ? {
          transform: `scale(${dropdownScale})`,
          transformOrigin: above ? "bottom left" : "top left",
        }
      : {};

  const pickOption = (opt) => {
    if (disabled) return;
    onChange?.({ target: { value: String(opt.id) } });
    setFocused(false);
  };

  return (
    <div
      className={
        "filterHeaderPresetSelect fw-400 d-flex align-items-center " +
        (focused ? "filterHeaderPresetSelect--focused" : "")
      }
      onClick={handleTriggerClick}
      ref={selectRef}
      style={{
        position: "relative",
        padding: "4px 28px 4px 10px",
        borderRadius: 6,
        cursor: disabled ? "not-allowed" : "pointer",
        minWidth: 120,
        maxWidth: 220,
        opacity: disabled ? 0.65 : 1,
        pointerEvents: disabled ? "none" : "auto",
        ...triggerStyle,
      }}
    >
      <p
        tabIndex={-1}
        className="filterHeaderPresetSelect__label m-0 fs-14"
        style={{
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          overflow: "hidden",
          lineHeight: 1.35,
          flex: 1,
        }}
      >
        {displayLabel}
      </p>
      {focused &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            ref={dropdownRef}
            className="filterHeaderPresetSelect__dropdown"
            tabIndex={-1}
            style={{
              position: "fixed",
              left: `${x}px`,
              width: `${width}px`,
              zIndex: 999999,
              padding: "4px 0",
              borderRadius: 6,
              maxHeight: 220,
              overflowY: "auto",
              ...(!dropdownScale
                ? { transformOrigin: above ? "bottom center" : "top center" }
                : {}),
              ...(above
                ? { bottom: `${window.innerHeight - y + 3}px` }
                : { top: `${y + height + 3}px` }),
              ...dropdownTransform,
              ...dropdownStyleRest,
            }}
          >
            {sanitizedOptions.length === 0 ? (
              <div className={`${PRESET_ROW_CLASS} text-muted small`}>—</div>
            ) : (
              sanitizedOptions.map((opt, index) => {
                const isCurrent = String(opt.id) === String(value ?? "");
                return (
                  <div
                    className={PRESET_ROW_CLASS}
                    role="option"
                    aria-selected={isCurrent}
                    onClick={(e) => {
                      e.stopPropagation();
                      pickOption(opt);
                    }}
                    key={`${opt.id}-${index}`}
                    style={{
                      cursor: "pointer",
                      fontSize: 14,
                      borderRadius: 4,
                    }}
                  >
                    <span className="flex-grow-1">{opt.label}</span>
                  </div>
                );
              })
            )}
          </div>,
          document.body
        )}
    </div>
  );
}

export default FilterHeaderPresetSelect;

const STANDARD_DAY_IDS = ["0", "1", "3", "7", "10", "15", "31"];

function labelsFromCommonUi(t) {
  const commonNs = { ns: "common" };
  return {
    "0": t("commonUi.today", { ...commonNs, defaultValue: "Today" }),
    "1": t("commonUi.yesterday", { ...commonNs, defaultValue: "Yesterday" }),
    "3": t("commonUi.last3Days", { ...commonNs, defaultValue: "Last 3 Days" }),
    "7": t("commonUi.last7Days", { ...commonNs, defaultValue: "Last 7 Days" }),
    "10": t("commonUi.last10Days", {
      ...commonNs,
      defaultValue: "Last 10 Days",
    }),
    "15": t("commonUi.last15Days", {
      ...commonNs,
      defaultValue: "Last 15 Days",
    }),
    "30": t("commonUi.last31Days", {
      ...commonNs,
      defaultValue: t("commonUi.last30Days", {
        ...commonNs,
        defaultValue: "Last 31 Days",
      }),
    }),
    "31": t("commonUi.last31Days", {
      ...commonNs,
      defaultValue: "Last 31 Days",
    }),
  };
}

export function buildDayOptionsCommonUi(t) {
  const L = labelsFromCommonUi(t);
  return STANDARD_DAY_IDS.map((id) => ({ id, label: L[id] }));
}

export function buildDayOptionsDashboard(t) {
  return buildDayOptionsCommonUi(t);
}

export function buildDayOptionsCompanyTxn(t) {
  const p = "company.transactionHistory.filterModal.dayOptions";
  return [
    { id: "0", label: t(`${p}.today`, { defaultValue: "Today" }) },
    { id: "1", label: t(`${p}.yesterday`, { defaultValue: "Yesterday" }) },
    { id: "3", label: t(`${p}.last3Days`, { defaultValue: "Last 3 Days" }) },
    { id: "7", label: t(`${p}.last7Days`, { defaultValue: "Last 7 Days" }) },
    { id: "10", label: t(`${p}.last10Days`, { defaultValue: "Last 10 Days" }) },
    { id: "15", label: t(`${p}.last15Days`, { defaultValue: "Last 15 Days" }) },
    { id: "30", label: t(`${p}.last30Days`, { defaultValue: "Last 30 Days" }) },
  ];
}

export function buildDayOptionsTwoWay(t) {
  const p = "twoWay.filter.daySelector";
  return [
    { id: "0", label: t(`${p}.today`, { defaultValue: "Today" }) },
    { id: "1", label: t(`${p}.yesterday`, { defaultValue: "Yesterday" }) },
    { id: "3", label: t(`${p}.last3Days`, { defaultValue: "Last 3 Days" }) },
    { id: "7", label: t(`${p}.last7Days`, { defaultValue: "Last 7 Days" }) },
    { id: "10", label: t(`${p}.last10Days`, { defaultValue: "Last 10 Days" }) },
    { id: "15", label: t(`${p}.last15Days`, { defaultValue: "Last 15 Days" }) },
    { id: "30", label: t(`${p}.last30Days`, { defaultValue: "Last 30 Days" }) },
  ];
}

export function buildDayOptionsUserFilterModal(t) {
  const p = "userFilterModal.days";
  return [
    { id: "0", label: t(`${p}.today`, { defaultValue: "Today" }) },
    { id: "1", label: t(`${p}.yesterday`, { defaultValue: "Yesterday" }) },
    { id: "3", label: t(`${p}.last3`, { defaultValue: "Last 3 Days" }) },
    { id: "7", label: t(`${p}.last7`, { defaultValue: "Last 7 Days" }) },
    { id: "10", label: t(`${p}.last10`, { defaultValue: "Last 10 Days" }) },
    { id: "15", label: t(`${p}.last15`, { defaultValue: "Last 15 Days" }) },
    { id: "30", label: t(`${p}.last30`, { defaultValue: "Last 30 Days" }) },
  ];
}

export function buildDayOptionsSmscFilter(t) {
  const p = "smscFilter.dayOptions";
  return [
    { id: "0", label: t(`${p}.today`, { defaultValue: "Today" }) },
    { id: "1", label: t(`${p}.yesterday`, { defaultValue: "Yesterday" }) },
    { id: "3", label: t(`${p}.last3Days`, { defaultValue: "Last 3 Days" }) },
    { id: "7", label: t(`${p}.last7Days`, { defaultValue: "Last 7 Days" }) },
    { id: "10", label: t(`${p}.last10Days`, { defaultValue: "Last 10 Days" }) },
    { id: "15", label: t(`${p}.last15Days`, { defaultValue: "Last 15 Days" }) },
    { id: "31", label: t(`${p}.last31Days`, { defaultValue: t(`${p}.last30Days`, { defaultValue: "Last 31 Days" }) }) },
  ];
}
